// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
  US_EVENT_TIMEZONES,
  EVENT_TIMEZONE_OTHER,
} from '../../domain/eventTimezones';
import { getEventFormSubmitError } from './eventFormSubmit';

const EVENT_IMAGE_HINT =
  'Card and header images must be PNG (any size). Upload them on the Event images tab.';

function EditEventDialogFields({
  form,
  onChange,
  onTimezoneSelectChange,
  availableImages,
  imagesLoading,
  selectCardImageValue,
  selectHeaderImageValue,
  timezoneSelectValue,
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label="Host"
        name="host"
        value={form.host}
        onChange={onChange}
        required
        fullWidth
        placeholder="e.g. NH Brewers Association"
      />
      <TextField
        label="Event name"
        name="name"
        value={form.name}
        onChange={onChange}
        required
        fullWidth
      />
      <TextField
        label="Event date"
        name="eventLocalDate"
        type="date"
        value={form.eventLocalDate}
        onChange={onChange}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <FormControl fullWidth required>
        <InputLabel id="event-timezone-preset-label">Timezone</InputLabel>
        <Select
          labelId="event-timezone-preset-label"
          label="Timezone"
          name="timezoneSelect"
          value={timezoneSelectValue}
          onChange={onTimezoneSelectChange}
        >
          {US_EVENT_TIMEZONES.map((z) => (
            <MenuItem key={z.value} value={z.value}>
              {z.label}
            </MenuItem>
          ))}
          <MenuItem value={EVENT_TIMEZONE_OTHER}>
            Other (IANA)…
          </MenuItem>
        </Select>
      </FormControl>
      {timezoneSelectValue === EVENT_TIMEZONE_OTHER && (
        <TextField
          label="IANA timezone"
          name="eventTimezone"
          value={form.eventTimezone}
          onChange={onChange}
          required
          fullWidth
          placeholder="e.g. Europe/London"
          helperText="Use a valid IANA time zone id."
        />
      )}
      <TextField
        label="Start time"
        name="eventStartTime"
        type="time"
        value={form.eventStartTime}
        onChange={onChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
        helperText="Optional. Local time in the timezone above; use with end time."
      />
      <TextField
        label="End time"
        name="eventEndTime"
        type="time"
        value={form.eventEndTime}
        onChange={onChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
        helperText="Optional. Leave start and end empty if times are not set yet."
      />
      <TextField
        label="Venue / location name"
        name="location"
        value={form.location}
        onChange={onChange}
        fullWidth
        placeholder="e.g. Tuckerman Brewing Co"
      />
      <TextField
        label="City, State"
        name="cityState"
        value={form.cityState}
        onChange={onChange}
        fullWidth
        placeholder="e.g. Conway, NH"
      />
      <TextField
        label="Latitude"
        name="latitude"
        value={form.latitude}
        onChange={onChange}
        fullWidth
        placeholder="e.g. 41.8781"
        helperText="Optional. Decimal degrees as text for now."
      />
      <TextField
        label="Longitude"
        name="longitude"
        value={form.longitude}
        onChange={onChange}
        fullWidth
        placeholder="e.g. -87.6298"
        helperText="Optional. Decimal degrees as text for now."
      />
      <TextField
        label="Event link"
        name="link"
        value={form.link}
        onChange={onChange}
        fullWidth
        placeholder="https://…"
        helperText="Optional. Must include https:// when set."
      />
      <TextField
        label="Link text"
        name="linkText"
        value={form.linkText ?? ''}
        onChange={onChange}
        fullWidth
        placeholder="e.g. Click here for details & to buy tickets"
        helperText="Optional. Shown on the card instead of the URL; still opens the event link above."
      />
      <TextField
        label="Description"
        name="description"
        value={form.description ?? ''}
        onChange={onChange}
        fullWidth
        multiline
        minRows={3}
      />
      <FormControl fullWidth required>
        <InputLabel id="event-card-image-label">Card image</InputLabel>
        <Select
          labelId="event-card-image-label"
          label="Card image"
          name="cardImage"
          value={selectCardImageValue}
          onChange={onChange}
          disabled={!availableImages.length && !imagesLoading}
        >
          {availableImages.map((imageName) => (
            <MenuItem key={imageName} value={imageName}>
              {imageName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth required>
        <InputLabel id="event-header-image-label">Header image</InputLabel>
        <Select
          labelId="event-header-image-label"
          label="Header image"
          name="headerImage"
          value={selectHeaderImageValue}
          onChange={onChange}
          disabled={!availableImages.length && !imagesLoading}
        >
          {availableImages.map((imageName) => (
            <MenuItem key={imageName} value={imageName}>
              {imageName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {imagesLoading && (
        <Alert severity="info">Loading image list from Storage…</Alert>
      )}
    </Stack>
  );
}

/**
 * Create or edit an event definition.
 */
function EditEventDialog({
  open,
  mode,
  submitting,
  errorMsg,
  form,
  availableImages = [],
  imagesLoading = false,
  onClose,
  onChange,
  onTimezoneSelectChange,
  onSubmit,
}) {
  const isEdit = mode === 'edit';
  // Dual-layer fallback: useEventFormDialog’s effect (see useEventFormDialog.js ~120–138)
  // syncs form.cardImage/headerImage when allowedImageChoices load; until then, these
  // Select value props use the first available image so MUI always shows a valid option.
  const trimmedCardImage = (form.cardImage ?? '').trim();
  const cardImageOk =
    Boolean(trimmedCardImage) && availableImages.includes(trimmedCardImage);
  const selectCardImageValue = cardImageOk
    ? trimmedCardImage
    : (availableImages[0] ?? '');
  const trimmedHeaderImage = (form.headerImage ?? '').trim();
  const headerImageOk =
    Boolean(trimmedHeaderImage) &&
    availableImages.includes(trimmedHeaderImage);
  const selectHeaderImageValue = headerImageOk
    ? trimmedHeaderImage
    : (availableImages[0] ?? '');

  const tzPresetValues = useMemo(
    () => US_EVENT_TIMEZONES.map((z) => z.value),
    [],
  );
  const timezoneSelectValue = tzPresetValues.includes(form.eventTimezone)
    ? form.eventTimezone
    : EVENT_TIMEZONE_OTHER;

  const canSubmit = useMemo(() => {
    return (
      getEventFormSubmitError(form, availableImages) === null && !submitting
    );
  }, [form, availableImages, submitting]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit event' : 'Add event'}</DialogTitle>
      <DialogContent>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          {EVENT_IMAGE_HINT}
        </Alert>
        <EditEventDialogFields
          form={form}
          onChange={onChange}
          onTimezoneSelectChange={onTimezoneSelectChange}
          availableImages={availableImages}
          imagesLoading={imagesLoading}
          selectCardImageValue={selectCardImageValue}
          selectHeaderImageValue={selectHeaderImageValue}
          timezoneSelectValue={timezoneSelectValue}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          variant="contained"
        >
          {submitting
            ? isEdit
              ? 'Saving…'
              : 'Creating…'
            : isEdit
              ? 'Save'
              : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditEventDialog;
